import React, { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import plusIconWhite from "../../../assets/admin/icons/plus-white.svg";
import {
   updatePickupSlots,
   queryClient,
   updateOpeningHours,
} from "../../../util/admin/httpAdmin";
import PrimaryButton from "../../shared/PrimaryButton";
import BoxBorder from "../../shared/BoxBorder";
import LoadingIndicatorButton from "../../shared/LoadingIndicatorButton";
import { validateTimeOverlap } from "../../../util/shared/validation";
import SuccessMessage from "../../shared/SuccessMessage";
import ErrorButton from "../../shared/ErrorButton";
import "./PickupSlotDayForm.css";
import editIconWhite from "../../../assets/admin/icons/edit-white.svg";
import SuccesButton from "../../shared/SuccessButton";

const settings = {
   pickup: {
      mutateFn: updatePickupSlots,
      successMessage: "Ophaalmomenten succesvol bijgewerkt!",
      queryKey: "pickupMoments",
      label: "Ophaalmomenten",
   },
   openinghours: {
      mutateFn: updateOpeningHours,
      successMessage: "Openingsuren succesvol bijgewerkt!",
      queryKey: "openingHours",
      label: "Openingsuren",
   },
};

const PickupSlotDayForm = ({
   day,
   editMode,
   previousData,
   onSuccessClose,
   type,
}) => {
   const [slots, setSlots] = useState(previousData || []);
   const [errorState, setErrorState] = useState({});
   const [successMessage, setSuccessMessage] = useState("");

   const generateTimeOptions = () => {
      const options = [];
      for (let i = 0; i < 24 * 4; i++) {
         const hours = Math.floor(i / 4)
            .toString()
            .padStart(2, "0");
         const minutes = ((i % 4) * 15).toString().padStart(2, "0");
         options.push(`${hours}:${minutes}`);
      }
      return options;
   };

   const timeOptions = generateTimeOptions();

   useEffect(() => {
      setSlots(previousData);
   }, [previousData]);

   const { mutate, isPending, isError, error } = useMutation({
      mutationFn: settings[type].mutateFn,
      onSuccess: () => {
         setSuccessMessage(settings[type].successMessage);
         queryClient.invalidateQueries(settings[type].queryKey);
         setTimeout(() => {
            onSuccessClose();
            setTimeout(() => {
               setSuccessMessage("");
            }, 100);
         }, 500);
      },
   });

   useEffect(() => {
      if (editMode && previousData && previousData[day]) {
         setSlots(previousData[day]);
      }
   }, [editMode, previousData, day]);

   const handleAddSlot = () => {
      const lastSlot = slots[slots.length - 1];
      const newStart = lastSlot
         ? addMinutesToTime(lastSlot.end, 15) // Voeg 15 minuten toe aan de eindtijd van het laatste slot
         : "14:00"; // Als er geen slots zijn, start bij 00:00
      const newEnd = addMinutesToTime(newStart, 15); // Nieuwe eindtijd is 15 minuten later
      const newSlot = { start: newStart, end: newEnd };
      setSlots((prevSlots) => [...prevSlots, newSlot]);
   };

   const addMinutesToTime = (time, minutes) => {
      const [hours, mins] = time.split(":").map(Number);
      const totalMinutes = hours * 60 + mins + minutes;
      const newHours = Math.floor(totalMinutes / 60) % 24;
      const newMinutes = totalMinutes % 60;
      return `${newHours.toString().padStart(2, "0")}:${newMinutes
         .toString()
         .padStart(2, "0")}`;
   };

   const handleRemoveSlot = (index) => {
      setSlots((prevSlots) => prevSlots.filter((_, i) => i !== index));
   };

   const handleChangeTime = (index, timeType, value) => {
      const updatedSlots = [...slots];
      updatedSlots[index] = { ...updatedSlots[index], [timeType]: value };
      setSlots(updatedSlots);
   };

   const handleFormSubmit = (e) => {
      e.preventDefault();

      let isValid = true;
      let newErrorState = {};

      for (let i = 0; i < slots.length; i++) {
         const { start, end } = slots[i];
         if (!start || !end) {
            isValid = false;
            newErrorState[`${day}-${i}`] = "Alle velden moeten ingevuld zijn.";
            break;
         }

         if (end <= start) {
            isValid = false;
            newErrorState[`${day}-${i}`] =
               "Eindtijd moet later zijn dan de starttijd.";
            break;
         }

         const validationError = validateTimeOverlap(slots, i, start, end);
         if (validationError) {
            isValid = false;
            newErrorState[`${day}-${i}`] = validationError;
            break;
         }
      }

      setErrorState(newErrorState);

      if (isValid) {
         mutate({ slots, day });
      }
   };

   return (
      <BoxBorder extraClasses="mxw-1000 m-a g-20">
         <h3>{day.charAt(0).toUpperCase() + day.slice(1)}</h3>
         {successMessage ? (
            <SuccessMessage title="Succes!" description={successMessage} />
         ) : (
            <form className="g-10">
               {slots.length === 0 ? (
                  <p>Je hebt geen {settings[type].label} gekozen</p>
               ) : (
                  slots.map((slot, index) => (
                     <div key={index} className="g-5">
                        <div className="g-1fr-130 a-i-b">
                           <div className="g-1-1 a-i-s">
                              <div className="g-5">
                                 <label htmlFor={`start-time-${day}-${index}`}>
                                    Starttijd
                                 </label>
                                 <select
                                    id={`start-time-${day}-${index}`}
                                    value={slot.start}
                                    onChange={(e) =>
                                       handleChangeTime(
                                          index,
                                          "start",
                                          e.target.value
                                       )
                                    }
                                    className={
                                       errorState[`${day}-${index}`]
                                          ? "border-red time-input"
                                          : "time-input"
                                    }
                                 >
                                    {timeOptions.map((time) => (
                                       <option key={time} value={time}>
                                          {time}
                                       </option>
                                    ))}
                                 </select>
                              </div>
                              <div className="g-5">
                                 <label htmlFor={`end-time-${day}-${index}`}>
                                    Eindtijd
                                 </label>
                                 <select
                                    id={`end-time-${day}-${index}`}
                                    value={slot.end}
                                    onChange={(e) =>
                                       handleChangeTime(
                                          index,
                                          "end",
                                          e.target.value
                                       )
                                    }
                                    className={
                                       errorState[`${day}-${index}`]
                                          ? "border-red time-input"
                                          : "time-input"
                                    }
                                 >
                                    {timeOptions.map((time) => (
                                       <option key={time} value={time}>
                                          {time}
                                       </option>
                                    ))}
                                 </select>
                              </div>
                           </div>
                           <div>
                              <ErrorButton
                                 onClick={(e) => {
                                    e.preventDefault();
                                    handleRemoveSlot(index);
                                 }}
                              >
                                 <div className="f-s">Verwijderen</div>
                              </ErrorButton>
                           </div>
                        </div>
                        {errorState[`${day}-${index}`] && (
                           <div className="c-r">
                              {errorState[`${day}-${index}`]}
                           </div>
                        )}
                     </div>
                  ))
               )}
               <div>
                  <PrimaryButton
                     onClick={(e) => {
                        e.preventDefault();
                        handleAddSlot();
                     }}
                  >
                     <div className="g-15-1fr f-s">
                        <img src={plusIconWhite} alt="add" />
                        <span className="flex-0-5">Voeg slot toe</span>
                     </div>
                  </PrimaryButton>
               </div>
               {isError && (
                  <div className="c-r">
                     {error?.info?.message ||
                        `Er is iets fout gegaan tijdens het verwerken van de ${settings[
                           type
                        ].label.toLowerCase()}`}
                  </div>
               )}
               <div>
                  <SuccesButton onClick={handleFormSubmit} disabled={isPending}>
                     <div className="g-15-1fr f-s">
                        <img src={editIconWhite} alt="edit"></img>
                        <span>
                           {settings[type].label}
                           {editMode ? "bijwerken" : "toevoegen"}
                           {isPending && <LoadingIndicatorButton />}
                        </span>
                     </div>
                  </SuccesButton>
               </div>
            </form>
         )}
      </BoxBorder>
   );
};

export default PickupSlotDayForm;
